import { UserProfile } from '@shared/types';

/**
 * Cookie-based authentication utilities
 * Tokens are now stored in HttpOnly cookies for security
 */

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthResponse {
  user: UserProfile;
  csrfToken?: string;
  error?: string;
}

/**
 * Login user and receive auth cookies
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  try {
    // Use fetch directly to avoid circular dependency
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      redirect: 'error',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(credentials)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    const data = await response.json();

    if (data.user) {
      // Store CSRF token in memory for subsequent requests
      if (data.csrfToken) {
        window.__CSRF_TOKEN = data.csrfToken;
      }
      
      // Store user profile for quick access (non-sensitive data)
      sessionStorage.setItem('userProfile', JSON.stringify(data.user));
    }

    return data;
  } catch (error) {
    console.error('Login error:', error);
    return { 
      user: null as any, 
      error: error instanceof Error ? error.message : 'Login failed' 
    };
  }
}

/**
 * Logout user and clear auth cookies
 */
export async function logout(): Promise<void> {
  try {
    // Use fetch directly to avoid circular dependency
    await fetch('/api/auth/logout', {
      method: 'POST',
      redirect: 'error',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': window.__CSRF_TOKEN || ''
      }
    });
  } finally {
    // Clear client-side data
    sessionStorage.removeItem('userProfile');
    delete window.__CSRF_TOKEN;
    
    // Redirect to login
    window.location.href = '/login';
  }
}

/**
 * Get current user from session storage
 * Note: This is just cached data, actual auth is via cookies
 */
export function getCurrentUser(): UserProfile | null {
  const cached = sessionStorage.getItem('userProfile');
  return cached ? JSON.parse(cached) : null;
}

/**
 * Check if user is authenticated
 * This is a quick check - actual auth happens server-side
 */
export function isAuthenticated(): boolean {
  return getCurrentUser() !== null;
}

/**
 * Get current user's role
 */
export function getUserRole(): string | null {
  const user = getCurrentUser();
  return user?.role || null;
}

/**
 * Check if user has a specific role
 */
export function hasRole(role: string): boolean {
  return getUserRole() === role;
}

/**
 * Check if user has access to a location
 */
export function hasLocationAccess(locationId: string): boolean {
  const user = getCurrentUser();
  return user?.accessibleLocationIds?.includes(locationId) || false;
}

/**
 * Refresh the access token
 * Called automatically by fetchWithAuth on 401 responses
 */
export async function refreshToken(): Promise<boolean> {
  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      redirect: 'error',
      credentials: 'include' // Include cookies
    });

    return response.ok;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
}

/**
 * Get CSRF token for mutations
 */
export function getCsrfToken(): string | null {
  return window.__CSRF_TOKEN || null;
}

// Extend window object for CSRF token storage
declare global {
  interface Window {
    __CSRF_TOKEN?: string;
  }
}
